import React, { useState, useMemo } from 'react';
import { Info } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';

// 2026 Base Pay Table (monthly) — key ranks × key YOS breakpoints
const BASE_PAY = {
  'E-1': { 0: 2017, 2: 2017, 4: 2017 },
  'E-2': { 0: 2261, 2: 2261, 4: 2261 },
  'E-3': { 0: 2378, 2: 2504, 4: 2652 },
  'E-4': { 0: 2633, 2: 2835, 4: 3050, 6: 3185, 8: 3185 },
  'E-5': { 0: 2872, 2: 3137, 4: 3325, 6: 3455, 8: 3619, 10: 3802, 12: 3987, 14: 4099 },
  'E-6': { 0: 3133, 2: 3489, 4: 3645, 6: 3819, 8: 4034, 10: 4228, 12: 4422, 14: 4617, 16: 4815, 18: 5015 },
  'E-7': { 0: 3623, 2: 3996, 4: 4151, 6: 4284, 8: 4503, 10: 4700, 12: 4899, 14: 5100, 16: 5304, 18: 5510, 20: 5720 },
  'E-8': { 0: 5204, 8: 5204, 10: 5420, 12: 5640, 14: 5865, 16: 6095, 18: 6330, 20: 6570, 22: 6815, 24: 7060 },
  'E-9': { 0: 6367, 10: 6367, 12: 6590, 14: 6820, 16: 7055, 18: 7295, 20: 7540, 22: 7790, 24: 8045, 26: 8310 },
  'O-1': { 0: 3826, 2: 3985, 4: 4819, 6: 4819 },
  'O-2': { 0: 4407, 2: 5020, 4: 5783, 6: 5980, 8: 5980 },
  'O-3': { 0: 5103, 2: 5783, 4: 6262, 6: 6762, 8: 7095, 10: 7352, 12: 7720 },
  'O-4': { 0: 5802, 2: 6712, 4: 7158, 6: 7522, 8: 7920, 10: 8382, 12: 8765, 14: 9135, 16: 9420 },
  'O-5': { 0: 6726, 2: 7578, 4: 8100, 6: 8195, 8: 8880, 10: 9285, 12: 9812, 14: 10210, 16: 10610, 18: 10960 },
  'O-6': { 0: 8065, 2: 8855, 4: 9435, 6: 9435, 8: 9520, 10: 10540, 12: 10920, 14: 11490, 16: 11990, 18: 12540, 20: 13150 },
};

// 2026 BAH rates (national averages, with/without dependents)
const BAH_AVERAGE = {
  'E-1': { without: 1024, with: 1248 }, 'E-2': { without: 1024, with: 1248 },
  'E-3': { without: 1024, with: 1248 }, 'E-4': { without: 1086, with: 1312 },
  'E-5': { without: 1152, with: 1395 }, 'E-6': { without: 1248, with: 1485 },
  'E-7': { without: 1345, with: 1578 }, 'E-8': { without: 1425, with: 1665 },
  'E-9': { without: 1512, with: 1758 },
  'O-1': { without: 1285, with: 1542 }, 'O-2': { without: 1395, with: 1648 },
  'O-3': { without: 1578, with: 1825 }, 'O-4': { without: 1755, with: 2015 },
  'O-5': { without: 1925, with: 2195 }, 'O-6': { without: 2085, with: 2365 },
};

// 2026 BAS rates (monthly)
const BAS = { enlisted: 460.25, officer: 316.98 };

const RANKS = Object.keys(BASE_PAY);

function getBasePay(rank, yos) {
  const table = BASE_PAY[rank];
  if (!table) return 0;
  const years = Object.keys(table).map(Number).sort((a, b) => a - b);
  let pay = 0;
  for (const y of years) {
    if (yos >= y) pay = table[y];
  }
  return pay;
}

const MilitaryPayCalculator = () => {
  const [rank, setRank] = useState('E-5');
  const [yearsOfService, setYearsOfService] = useState(6);
  const [hasDependents, setHasDependents] = useState(true);
  const [filingStatus, setFilingStatus] = useState('single'); // for tax calculation

  const results = useMemo(() => {
    const yos = Math.max(0, Math.min(40, yearsOfService));
    const basePay = getBasePay(rank, yos);
    const isOfficer = rank.startsWith('O-');
    const bas = isOfficer ? BAS.officer : BAS.enlisted;
    const bahData = BAH_AVERAGE[rank] || { without: 1200, with: 1450 };
    const bah = hasDependents ? bahData.with : bahData.without;

    const totalPay = basePay + bah + bas;
    const taxFreeAllowances = bah + bas;

    // Estimate federal tax on base pay only (simplified brackets)
    const annualTaxable = basePay * 12;
    let federalTax;
    if (filingStatus === 'single') {
      if (annualTaxable <= 11600) federalTax = annualTaxable * 0.10;
      else if (annualTaxable <= 47150) federalTax = 1160 + (annualTaxable - 11600) * 0.12;
      else if (annualTaxable <= 100525) federalTax = 5426 + (annualTaxable - 47150) * 0.22;
      else federalTax = 17169 + (annualTaxable - 100525) * 0.24;
    } else {
      if (annualTaxable <= 23200) federalTax = annualTaxable * 0.10;
      else if (annualTaxable <= 94300) federalTax = 2320 + (annualTaxable - 23200) * 0.12;
      else if (annualTaxable <= 201050) federalTax = 10852 + (annualTaxable - 94300) * 0.22;
      else federalTax = 34337 + (annualTaxable - 201050) * 0.24;
    }

    const monthlyTax = federalTax / 12;
    const takeHome = totalPay - monthlyTax;
    const taxAdvantage = taxFreeAllowances * 12 * 0.22; // Estimated 22% bracket savings
    const civilianEquivalent = totalPay + (taxFreeAllowances * 0.25); // Gross-up allowances

    return {
      basePay, bah, bas, totalPay, taxFreeAllowances,
      monthlyTax: Math.round(monthlyTax),
      takeHome: Math.round(takeHome),
      annualTotalPay: totalPay * 12,
      annualTaxAdvantage: Math.round(taxAdvantage),
      civilianEquivalent: Math.round(civilianEquivalent),
    };
  }, [rank, yearsOfService, hasDependents, filingStatus]);

  return (
    <div className="space-y-6" data-testid="military-pay-calculator">
      {/* Inputs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <Label className="text-sm font-medium text-slate-700">Rank</Label>
          <select
            value={rank}
            onChange={(e) => setRank(e.target.value)}
            className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
            data-testid="pay-rank"
          >
            {RANKS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-sm font-medium text-slate-700">Years of Service</Label>
          <input
            type="number"
            min={0}
            max={40}
            value={yearsOfService}
            onChange={(e) => setYearsOfService(Number(e.target.value))}
            className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 text-sm"
            data-testid="pay-yos"
          />
        </div>
        <div>
          <Label className="text-sm font-medium text-slate-700">Dependents</Label>
          <div className="flex gap-2 mt-1">
            {[{ v: true, l: 'Yes' }, { v: false, l: 'No' }].map(opt => (
              <button
                key={String(opt.v)}
                onClick={() => setHasDependents(opt.v)}
                className={`flex-1 h-10 rounded-md border text-sm font-medium transition-all ${
                  hasDependents === opt.v
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-slate-700 border-slate-200'
                }`}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-sm font-medium text-slate-700">Filing Status</Label>
          <select
            value={filingStatus}
            onChange={(e) => setFilingStatus(e.target.value)}
            className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 text-sm bg-white"
          >
            <option value="single">Single</option>
            <option value="married">Married Filing Jointly</option>
          </select>
        </div>
      </div>

      {/* Pay Breakdown */}
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-white">
        <CardContent className="p-5">
          <div className="space-y-2 mb-4">
            {[
              { label: 'Base Pay', value: results.basePay, taxable: true },
              { label: `BAH (${hasDependents ? 'w/ dependents' : 'w/o dependents'})`, value: results.bah, taxable: false },
              { label: `BAS (${rank.startsWith('O-') ? 'Officer' : 'Enlisted'})`, value: results.bas, taxable: false },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-700">{item.label}</span>
                  {!item.taxable && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Tax-Free</span>}
                </div>
                <span className="text-sm font-semibold text-slate-900">${item.value.toLocaleString()}/mo</span>
              </div>
            ))}
            <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
              <span className="text-sm text-red-600">Est. Federal Tax</span>
              <span className="text-sm font-semibold text-red-600">-${results.monthlyTax.toLocaleString()}/mo</span>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="text-center p-3 bg-white rounded-lg border">
              <p className="text-[10px] text-slate-500">Total Monthly</p>
              <p className="text-xl font-bold text-blue-700" data-testid="total-monthly-pay">${results.totalPay.toLocaleString()}</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border">
              <p className="text-[10px] text-slate-500">Take-Home</p>
              <p className="text-xl font-bold text-green-700">${results.takeHome.toLocaleString()}</p>
            </div>
            <div className="text-center p-3 bg-white rounded-lg border">
              <p className="text-[10px] text-slate-500">Annual Tax Advantage</p>
              <p className="text-xl font-bold text-emerald-700">${results.annualTaxAdvantage.toLocaleString()}</p>
            </div>
            <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-100">
              <p className="text-[10px] text-amber-600">Civilian Equivalent</p>
              <p className="text-xl font-bold text-amber-700">${results.civilianEquivalent.toLocaleString()}/mo</p>
            </div>
          </div>

          <div className="mt-3 flex items-start gap-2 text-xs text-blue-700 bg-blue-100/50 p-2.5 rounded-lg">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>BAH shown is the national average. Actual BAH varies significantly by zip code. BAH and BAS are tax-free — the civilian equivalent shows what you'd need to earn pre-tax in the civilian world for equal value.</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MilitaryPayCalculator;
